import { useId, useRef, useState } from "react";
import { Badge, Button, Dialog, DialogActions, Field, Table } from "@studio";
import { api, errorMessage } from "../lib/api";
import { can } from "../lib/access";
import { useAuth } from "../lib/auth";
import { compact, fmtDateTime, fmtTime } from "../lib/format";
import { keys, useAction, useReleaseCommands, useReleaseMachines, useReleases } from "../lib/queries";
import type { AppRelease, ReleaseCommand, ReleaseMachine } from "../lib/types";
import { InfoRow, Loading, Panel, PanelError } from "../console/ui";

/**
 * 브릿지 릴리즈·원격 업데이트 (시안 06a1acb release-management.tsx 를 실제 API 로).
 * - ReleaseManagementScreen: 머신 › 버전 배포. 업로드/가져오기, 배포/회수, 버전 분포.
 * - MachineVersionPanel: 머신 상세 › 버전. 업데이트 요청·진행·취소·이력.
 * - CustomerUpdateDialog: 고객사 상세 › 개요 "모든 머신 업데이트" 대상/건너뜀 미리보기.
 */

const commandStatus: Record<string, [string, "blue" | "amber" | "teal" | "coral" | "neutral"]> = {
  queued: ["요청 대기", "amber"],
  sent: ["전송됨", "blue"],
  accepted: ["수락됨", "blue"],
  waiting_idle: ["작업 끝나기 기다림", "amber"],
  downloading: ["다운로드 중", "blue"],
  verifying: ["검증 중", "blue"],
  installing: ["설치 중", "blue"],
  succeeded: ["성공", "teal"],
  failed: ["실패", "coral"],
  cancelled: ["취소됨", "neutral"],
  expired: ["만료", "neutral"],
};
const OPEN = new Set(["queued", "sent", "accepted", "waiting_idle", "downloading", "verifying", "installing"]);
const CANCELABLE = new Set(["queued", "sent", "accepted", "waiting_idle"]);
const releaseStatus: Record<AppRelease["status"], [string, "teal" | "neutral" | "amber"]> = { published: ["배포 중", "teal"], draft: ["초안", "amber"], withdrawn: ["회수됨", "neutral"] };

export function versionStage(m: ReleaseMachine): string {
  if (!m.supportsRemoteUpdate) return "직접 설치 필요";
  if (m.lastUpdate && OPEN.has(m.lastUpdate.status)) return commandStatus[m.lastUpdate.status]?.[0] ?? m.lastUpdate.status;
  if (!m.updateAvailable) return m.appVersion ? "이미 최신 버전" : "버전 미확인";
  return "업데이트 가능";
}

export function ReleaseManagementScreen() {
  const me = useAuth((s) => s.me);
  const releases = useReleases();
  const machines = useReleaseMachines();
  const [mode, setMode] = useState<"" | "upload" | "import">("");
  const [msg, setMsg] = useState<string | null>(null);
  const inv = [keys.releases, ["admin", "releases"]];
  const setStatus = useAction((v: { id: string; action: "publish" | "withdraw" }) => api.post(`/admin/releases/${v.id}/${v.action}`), inv);
  const canDeploy = can(me, "MACHINE_DEPLOY");
  const rows = releases.data?.items ?? [];
  const dist = new Map<string, { n: number; supported: number }>();
  for (const m of machines.data?.machines ?? []) {
    const v = m.appVersion ?? "미확인";
    const cur = dist.get(v) ?? { n: 0, supported: 0 };
    dist.set(v, { n: cur.n + 1, supported: cur.supported + (m.supportsRemoteUpdate ? 1 : 0) });
  }
  const latest = machines.data?.latest?.version;
  const run = (p: Promise<unknown>, done: string) => p.then(() => setMsg(done)).catch((e) => setMsg(errorMessage(e)));
  return (
    <div className="lwd-stack">
      {msg && <InfoRow title={msg} />}
      <Panel
        title="릴리즈"
        extra={
          canDeploy && (
            <div className="lwd-actions">
              <Button size="sm" variant="secondary" onClick={() => setMode("import")}>
                빌드 폴더에서 가져오기
              </Button>
              <Button size="sm" onClick={() => setMode("upload")}>
                릴리즈 업로드
              </Button>
            </div>
          )
        }
      >
        {releases.isLoading ? (
          <Loading />
        ) : releases.error ? (
          <PanelError error={errorMessage(releases.error)} onRetry={() => void releases.refetch()} />
        ) : rows.length === 0 ? (
          <InfoRow title="등록된 릴리즈가 없습니다" description="설치 파일을 올리거나 빌드 폴더에서 가져오세요." />
        ) : (
          <Table>
            <thead>
              <tr>
                <th>버전 / 크기</th>
                <th>SHA256</th>
                <th>메모</th>
                <th>상태 / 배포 시각</th>
                {canDeploy && <th>관리</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>
                    {r.version}
                    <small className="meta-crew-sub">{compact(Math.round(r.size / 1024))} KB</small>
                  </td>
                  <td>
                    <code>{r.sha256.slice(0, 8)}…</code>
                  </td>
                  <td>{r.notes ?? "-"}</td>
                  <td>
                    <Badge tone={releaseStatus[r.status][1]}>{releaseStatus[r.status][0]}</Badge>
                    <small className="meta-crew-sub">{r.publishedAt ? fmtDateTime(r.publishedAt) : fmtDateTime(r.createdAt)}</small>
                  </td>
                  {canDeploy && (
                    <td>
                      <Button size="sm" variant="secondary" disabled={setStatus.isPending} onClick={() => run(setStatus.mutateAsync({ id: r.id, action: r.status === "published" ? "withdraw" : "publish" }), r.status === "published" ? `${r.version}을 회수했습니다.` : `${r.version}을 배포했습니다.`)}>
                        {r.status === "published" ? "회수" : "배포"}
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Panel>
      <Panel title="버전 분포">
        {machines.isLoading ? (
          <Loading />
        ) : machines.error ? (
          <PanelError error={errorMessage(machines.error)} onRetry={() => void machines.refetch()} />
        ) : (
          <>
            {[...dist.entries()]
              .sort((a, b) => b[0].localeCompare(a[0]))
              .map(([v, d]) => (
                <InfoRow key={v} title={`${v}${v === latest ? " · 최신" : latest ? " · 업데이트 가능" : ""}`} description={`${d.n}대${d.supported < d.n ? ` · 원격 업데이트 미지원 ${d.n - d.supported}대 (직접 설치 필요)` : ""}`} />
              ))}
            {dist.size === 0 && <InfoRow title="연결된 머신이 없습니다" />}
            <div className="lwd-panel-body">
              <p className="meta-crew-muted">특정 머신의 업데이트는 머신 상세 › 버전에서, 고객사 전체는 고객사 상세 › 개요에서 요청합니다.</p>
            </div>
          </>
        )}
      </Panel>
      <ReleaseAddDialog mode={mode} onClose={() => setMode("")} onDone={(m) => setMsg(m)} />
    </div>
  );
}

const mb = (n: number) => `${(n / 1_048_576).toFixed(1)}MB`;

function ReleaseAddDialog({ mode, onClose, onDone }: { mode: "" | "upload" | "import"; onClose: () => void; onDone: (msg: string) => void }) {
  const formId = useId();
  const [err, setErr] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  // 업로드 진행률 (설치 파일이 100MB 가까이 돼 몇십 초 걸린다). 100% 뒤에는 서버가 해시·저장하는 동안 "확인 중".
  const sending = useRef(false);
  const [progress, setProgress] = useState<{ sent: number; total: number } | null>(null);
  const add = useAction(async (v: { file?: File | null; fileName?: string; notes: string }) => {
    if (mode === "upload") {
      if (!v.file) throw new Error("설치 파일을 고르세요.");
      setProgress({ sent: 0, total: v.file.size });
      return api
        .uploadWithProgress<AppRelease>("/admin/releases", v.file, { fields: v.notes ? { notes: v.notes } : {}, onProgress: (sent, total) => setProgress({ sent, total }) })
        .finally(() => setProgress(null));
    }
    return api.post<AppRelease>("/admin/releases/import", { fileName: v.fileName, notes: v.notes || undefined });
  }, [keys.releases]);
  return (
    <Dialog
      open={!!mode}
      onOpenChange={(v) => !v && !add.isPending && onClose()}
      title={mode === "upload" ? "릴리즈 업로드" : "빌드 폴더에서 가져오기"}
      description={mode === "upload" ? "Meta-Crew-Worker-Setup-<버전>-x64.exe 를 올립니다. 등록 후 초안이며 배포를 눌러야 업데이트 대상이 됩니다." : "코어 서버의 INSTALLER_DIR 에 있는 설치 파일 이름을 적습니다."}
      footer={
        <DialogActions
          secondary={{ label: "취소", onClick: onClose, disabled: add.isPending }}
          primary={{ label: add.isPending ? (mode === "upload" ? "올리는 중…" : "등록 중…") : "등록", type: "submit", form: formId, disabled: add.isPending }}
          note={err ?? undefined}
        />
      }
    >
      <form
        id={formId}
        className="mcc-form"
        onSubmit={(e) => {
          e.preventDefault();
          // 다시 그리기 전에 두 번 눌러도 한 번만 보낸다
          if (sending.current) return;
          sending.current = true;
          setErr(null);
          const d = new FormData(e.currentTarget);
          add
            .mutateAsync({ file, fileName: String(d.get("fileName") ?? ""), notes: String(d.get("notes") ?? "") })
            .then((r) => {
              onDone(`${r.version} 릴리즈를 등록했습니다 (초안).`);
              onClose();
            })
            .catch((er) => setErr(errorMessage(er)))
            .finally(() => (sending.current = false));
        }}
      >
        {mode === "upload" ? (
          <>
            <Field label="설치 파일" type="file" accept=".exe" required disabled={add.isPending} onChange={(e) => setFile(e.currentTarget.files?.[0] ?? null)} />
            {progress && (
              <div role="status" aria-live="polite">
                <progress max={progress.total || 1} value={progress.sent} aria-label="업로드 진행률" style={{ width: "100%" }} />
                <small>
                  {progress.sent < progress.total
                    ? `올리는 중 ${Math.floor((progress.sent / (progress.total || 1)) * 100)}% · ${mb(progress.sent)} / ${mb(progress.total)}`
                    : "업로드 완료 · 서버에서 파일을 확인하는 중…"}
                </small>
              </div>
            )}
          </>
        ) : (
          <Field name="fileName" label="빌드 파일 이름" placeholder="Meta-Crew-Worker-Setup-0.3.8-x64.exe" required />
        )}
        <Field name="notes" label="릴리즈 메모" placeholder="변경 내용" />
      </form>
    </Dialog>
  );
}

/** 머신 상세 › 버전: 현재 버전, 업데이트 요청·진행률·취소, 이력. */
export function MachineVersionPanel({ customerId, machineId, canUpdate }: { customerId: string; machineId: string; canUpdate: boolean }) {
  const rel = useReleaseMachines(customerId);
  const cmds = useReleaseCommands({ machineId });
  const [msg, setMsg] = useState<string | null>(null);
  const [confirm, setConfirm] = useState(false);
  const inv = [keys.releaseMachines(customerId), keys.releaseMachines(""), ["admin", "releases"]];
  const request = useAction(() => api.post<ReleaseCommand>(`/admin/machines/${machineId}/update`, {}), inv);
  const cancel = useAction((id: string) => api.post(`/admin/releases/commands/${id}/cancel`), inv);
  if (rel.isLoading) return <Loading />;
  if (rel.error) return <PanelError error={errorMessage(rel.error)} onRetry={() => void rel.refetch()} />;
  const m = rel.data?.machines.find((x) => x.machineId === machineId);
  const latest = rel.data?.latest;
  if (!m) return <InfoRow title="버전 정보가 없습니다" description="페어링된 머신만 표시됩니다." />;
  const stage = versionStage(m);
  const open = m.lastUpdate && OPEN.has(m.lastUpdate.status) ? m.lastUpdate : null;
  const run = (p: Promise<unknown>, done: string) => p.then(() => setMsg(done)).catch((e) => setMsg(errorMessage(e)));
  return (
    <div className="lwd-stack">
      <Panel title="프로그램 버전">
        <InfoRow title={m.appVersion ? (latest && m.updateAvailable ? `${m.appVersion} → ${latest.version}` : `${m.appVersion}${latest && !m.updateAvailable ? " · 최신 버전" : ""}`) : "버전 미확인"} description={stage}>
          {canUpdate && m.supportsRemoteUpdate && m.updateAvailable && !open && (
            <Button size="sm" onClick={() => setConfirm(true)}>
              업데이트
            </Button>
          )}
        </InfoRow>
        <div className="lwd-panel-body lwd-update">
          <progress max={100} value={open ? (open.progressPct ?? 0) : m.lastUpdate?.status === "succeeded" ? 100 : 0} aria-label="업데이트 진행률" />
          {open && (
            <>
              <Badge tone={commandStatus[open.status]?.[1] ?? "blue"}>
                {commandStatus[open.status]?.[0] ?? open.status}
                {open.progressPct !== null && open.progressPct !== undefined ? ` ${open.progressPct}%` : ""}
              </Badge>
              {open.message && <small className="meta-crew-muted">{open.message}</small>}
              {canUpdate && CANCELABLE.has(open.status) && (
                <Button size="sm" variant="secondary" onClick={() => run(cancel.mutateAsync(open.id), "업데이트 요청을 취소했습니다.")}>
                  업데이트 요청 취소
                </Button>
              )}
            </>
          )}
          {!m.supportsRemoteUpdate && <small className="meta-crew-muted">이 브릿지는 원격 업데이트를 지원하지 않습니다. 0.3.6 이상을 한 번 직접 설치하면 이후부터 원격으로 올릴 수 있습니다.</small>}
          {msg && <small>{msg}</small>}
        </div>
      </Panel>
      <Panel title="업데이트 이력">
        {(cmds.data?.items ?? []).length === 0 && <InfoRow title="이력 없음" />}
        {(cmds.data?.items ?? []).slice(0, 20).map((k) => (
          <InfoRow key={k.id} title={`${fmtTime(k.createdAt)} · ${k.fromVersion ?? "?"} → ${k.targetVersion}`} description={k.message ?? ""}>
            <Badge tone={commandStatus[k.status]?.[1] ?? "neutral"}>{commandStatus[k.status]?.[0] ?? k.status}</Badge>
          </InfoRow>
        ))}
      </Panel>
      <Dialog
        open={confirm}
        onOpenChange={(v) => !v && setConfirm(false)}
        title="머신 업데이트"
        description={`${m.label} · 대상 버전 ${latest?.version ?? "-"}`}
        footer={
          <DialogActions
            secondary={{ label: "취소", onClick: () => setConfirm(false) }}
            primary={{
              label: "업데이트 요청",
              disabled: request.isPending,
              onClick: () => {
                setConfirm(false);
                void run(request.mutateAsync(), "업데이트를 요청했습니다. 진행 중인 작업이 끝나면 설치합니다.");
              },
            }}
          />
        }
      >
        <div className="lwd-stack">
          <p>진행 중인 작업이 끝나면 설치합니다. 오프라인 머신은 재접속 후 요청을 받습니다.</p>
          <p className="meta-crew-muted">요청 유효기간 24시간 · 같은 머신의 대기 요청은 새 요청으로 대체됩니다.</p>
        </div>
      </Dialog>
    </div>
  );
}

/** 고객사 상세 › 개요: 모든 머신 업데이트 (대상·건너뜀 미리보기 → 요청). */
export function CustomerUpdateDialog({ customerId, customerName, open, onClose, onDone }: { customerId: string; customerName: string; open: boolean; onClose: () => void; onDone: (msg: string) => void }) {
  const rel = useReleaseMachines(customerId, open);
  const [err, setErr] = useState<string | null>(null);
  const request = useAction(() => api.post<{ requested: unknown[]; skipped: { label: string; reason: string }[] }>(`/admin/customers/${customerId}/update`, {}), [keys.releaseMachines(customerId), ["admin", "releases"]]);
  const latest = rel.data?.latest;
  const targets = (rel.data?.machines ?? []).filter((m) => m.supportsRemoteUpdate && m.updateAvailable && !(m.lastUpdate && OPEN.has(m.lastUpdate.status)));
  return (
    <Dialog
      open={open}
      onOpenChange={(v) => !v && onClose()}
      title="모든 머신 업데이트"
      description={`${customerName} · 대상 버전 ${latest?.version ?? "배포본 없음"}`}
      footer={
        <DialogActions
          secondary={{ label: "취소", onClick: onClose }}
          primary={{
            label: "업데이트 요청",
            disabled: request.isPending || !latest || targets.length === 0,
            onClick: () => {
              setErr(null);
              request
                .mutateAsync()
                .then((r) => {
                  onDone(`${r.requested.length}대에 요청${r.skipped.length ? ` · ${r.skipped.length}대 건너뜀` : ""}`);
                  onClose();
                })
                .catch((e) => setErr(errorMessage(e)));
            },
          }}
          note={err ?? undefined}
        />
      }
    >
      <div className="lwd-stack">
        <p>진행 중인 작업이 끝나면 설치합니다. 오프라인 머신은 재접속 후 요청을 받습니다.</p>
        {rel.isLoading ? (
          <Loading />
        ) : (
          (rel.data?.machines ?? []).map((m) => <InfoRow key={m.machineId} title={m.label} description={`${m.appVersion ?? "버전 미확인"} · ${versionStage(m)}`} />)
        )}
        {rel.data && rel.data.machines.length === 0 && <InfoRow title="페어링된 머신이 없습니다" />}
        <p className="meta-crew-muted">요청 유효기간 24시간 · 같은 머신의 대기 요청은 새 요청으로 대체됩니다.</p>
      </div>
    </Dialog>
  );
}
